import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, ArrowRight, ShieldCheck, Crosshair, Building2, Target, ScrollText } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { Footer } from "@/components/BusinessSection";
import { PolicyBar } from "@/components/PolicyBar";
import { PartnerNudge } from "@/components/PartnerNudge";
import { MEDIA } from "@/lib/media";

const CATEGORIES = [
  { key: "Optics", title: "Optics", desc: "Scopes, binoculars & rangefinders.", img: MEDIA.gear.optics },
  { key: "Archery", title: "Archery", desc: "Bows, arrows & accessories.", img: MEDIA.gear.archery },
  { key: "Packs", title: "Packs", desc: "Hunting packs & storage.", img: MEDIA.gear.packs },
  { key: "Clothing", title: "Apparel", desc: "Clothing for every season.", img: MEDIA.gear.apparel },
  { key: "Boots", title: "Boots", desc: "Performance, comfort & durability.", img: MEDIA.gear.boots },
  { key: "Accessories", title: "Accessories", desc: "Tools, safety, calls & more.", img: MEDIA.gear.accessories },
];

const FIREARM_CAPS = [
  { icon: Crosshair, title: "Specifications", desc: "Compare details." },
  { icon: Building2, title: "Manufacturer Info", desc: "Trusted sources." },
  { icon: Target, title: "Fit for Hunt", desc: "Make informed decisions." },
  { icon: ScrollText, title: "Regulations", desc: "Stay compliant." },
];

export default function GearMarketplace() {
  const navigate = useNavigate();
  const goCat = (c) => navigate(`/marketplace?category=${encodeURIComponent(c)}`);

  return (
    <div className="bg-paper min-h-screen" data-testid="gear-page">
      <SiteHeader />

      {/* Split hero */}
      <section className="relative bg-bone overflow-hidden" data-testid="gear-hero">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 items-stretch">
          <div className="px-5 sm:px-10 pt-8 pb-12 lg:py-20 flex flex-col justify-center relative z-10">
            <nav className="flex items-center gap-1.5 text-xs text-charcoal/50 mb-5" aria-label="Breadcrumb">
              <Link to="/" className="hover:text-blaze">Home</Link><ChevronRight size={13} />
              <span className="text-forest font-medium">Gear &amp; Marketplace</span>
            </nav>
            <span className="hr-overline text-blaze">Sporting Equipment</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.9] tracking-tight text-forest mt-3">Find the gear for your hunt.</h1>
            <p className="text-base text-charcoal/70 mt-5 max-w-md">Research, compare, and shop trusted gear from top brands and licensed sellers.</p>
            <button onClick={() => navigate("/marketplace")} className="mt-7 inline-flex w-fit items-center gap-2 bg-blaze text-white px-6 py-3.5 font-semibold uppercase tracking-wider text-sm hover:bg-blaze-light transition-colors" data-testid="gear-shop-all">
              Shop the marketplace <ArrowRight size={16} />
            </button>
          </div>
          <div className="relative min-h-[240px] lg:min-h-0">
            <img src={MEDIA.gearHero} alt="Hunters glassing a mountain ridge at sunset" className="absolute inset-0 w-full h-full object-cover" loading="eager" />
            <div className="absolute inset-0 bg-gradient-to-r from-bone via-bone/10 to-transparent hidden lg:block" />
          </div>
        </div>
      </section>

      <main className="max-w-[1400px] mx-auto px-5 sm:px-10 py-14 space-y-16">
        {/* Category cards */}
        <section data-testid="gear-categories">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {CATEGORIES.map((c, i) => (
              <motion.button
                key={c.key} onClick={() => goCat(c.key)}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.04 }}
                className="group relative h-[300px] overflow-hidden text-left" data-testid={`gear-cat-${c.key.toLowerCase()}`}>
                <img src={c.img} alt={c.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/95 via-charcoal/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4">
                  <h3 className="font-serif text-2xl text-bone leading-tight">{c.title}</h3>
                  <p className="text-bone/70 text-xs mt-1">{c.desc}</p>
                </div>
              </motion.button>
            ))}
          </div>
        </section>

        <PartnerNudge preferType="brand" label="Featured gear partner" />

        {/* Firearms Research band */}
        <section className="bg-charcoal text-bone grid lg:grid-cols-[0.9fr_1.1fr] overflow-hidden" data-testid="firearms-research">
          <div className="relative min-h-[240px]">
            <img src={MEDIA.firearmsBand} alt="Bolt-action hunting rifle on rock" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
          </div>
          <div className="p-7 sm:p-10">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-5">
              <div className="max-w-lg">
                <span className="hr-overline text-blaze">Firearms Research</span>
                <h2 className="font-serif text-4xl sm:text-5xl leading-none mt-2">Research with confidence.</h2>
                <p className="text-bone/70 text-sm mt-4">Get detailed specifications, manufacturer information, fit-for-hunt insights, and applicable regulations — all in one place.</p>
              </div>
              <button
                onClick={() => navigate("/firearms")}
                className="shrink-0 inline-flex items-center gap-2 bg-bone text-charcoal px-5 py-3 font-semibold text-sm hover:bg-white transition-colors" data-testid="firearms-explore">
                Explore Firearms Research <ArrowRight size={16} />
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-9 pt-7 border-t border-white/10">
              {FIREARM_CAPS.map((c) => {
                const Icon = c.icon;
                return (
                  <div key={c.title} className="text-center sm:text-left" data-testid={`firearm-cap-${c.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                    <span className="inline-grid w-10 h-10 place-items-center rounded-full border border-white/15 text-blaze mb-2"><Icon size={18} /></span>
                    <p className="font-semibold text-sm">{c.title}</p>
                    <p className="text-bone/55 text-xs">{c.desc}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Compliance panel */}
        <section className="bg-white border border-saddle/20 p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5 justify-between" data-testid="compliance-panel">
          <div className="flex items-start gap-4">
            <span className="w-12 h-12 rounded-full border border-saddle/30 grid place-items-center shrink-0"><ShieldCheck size={22} className="text-forest" /></span>
            <div>
              <h3 className="font-serif text-2xl text-forest">Licensed sellers. Compliant purchases.</h3>
              <p className="text-charcoal/65 text-sm mt-1.5 max-w-2xl">HomeRange does not process firearm sales. Regulated purchases must be completed with independent licensed sellers, subject to applicable laws and eligibility requirements.</p>
            </div>
          </div>
          <Link to="/terms" className="shrink-0 inline-flex items-center gap-1.5 text-blaze font-semibold text-sm hover:underline" data-testid="compliance-resources">View Compliance Resources <ArrowRight size={15} /></Link>
        </section>
      </main>

      <PolicyBar />
      <Footer />
    </div>
  );
}
